import {css} from '@emotion/react';
import {currencyFormatter} from '@dnhb/formatters';
import { Fragment, useEffect } from 'react';
import {useDispatch, useSelector} from 'shared/hooks';
import {useLocation} from 'react-router-dom';
import analytics from 'shared/utility/analytics';
import {Collapsable, Display, DisplayLink, Heading, LabelText} from 'shared/components';
import {NAVIGATE, SYNC} from 'shared/wizard/actions';

const WizardFinal = () => {
  const {state} = useLocation();
  const dispatch = useDispatch();
  const {
    pages,
    input: {
      new_regulations,
      has_student_loan,
      student_loan_monthly_payment,
      partner_student_loan_monthly_payment,
      has_total_credits,
      total_credits,
      has_partner_alimony,
      partner_alimony,
      has_ground_rent,
      ground_rent,
      has_private_lease,
      private_lease,
      has_partner_private_lease,
      partner_private_lease,
      applicant_has_partner,
      applicant_dob,
      partner_dob,
      applicant_is_entrepreneur,
      partner_is_entrepreneur,
      energy_label,
    },
    output: {
      applicant_income,
      applicant_aow_income,
      partner_income,
      partner_aow_income,
      show_applicant_pension_income,
      show_partner_pension_income,
    },
    meta: { energy_labels, energy_labels_next_year },
  } = useSelector(state => state)
  const currentPageIndex = pages.indexOf(state?.page);
  const hasExpenses =
    has_student_loan || has_total_credits || has_partner_alimony || has_ground_rent || has_private_lease;

  useEffect(() => {
    dispatch({type: SYNC, syncing: false, recalculate: null});
    analytics.send('Bereken', 'Resultaat');
  }, []);

  return (
    <Fragment>
      <Heading>Uitgangspunten</Heading>
      <LabelText
        css={css`
          margin-bottom: 24px;
        `}
      >
        Controleer hieronder je gegevens en pas ze aan als dat nodig is.
      </LabelText>
      <Collapsable title="Jouw gegevens" openByDefault>
        <Display label="Geboortedatum" value={applicant_dob} />
        <Display label="Ondernemer" value={applicant_is_entrepreneur} />
        <Display label="Bruto jaarinkomen" value={currencyFormatter(applicant_income)} />
        {show_applicant_pension_income && (
          <Display label="Bruto AOW jaarinkomen" value={currencyFormatter(applicant_aow_income)} />
        )}
        <DisplayLink onClick={() => dispatch({type: NAVIGATE, recalculate: currentPageIndex, index: 0})}>
          Gegevens aanpassen
        </DisplayLink>
      </Collapsable>
      {applicant_has_partner && (
        <Collapsable title="Gegevens partner">
          <Display label="Geboortedatum" value={partner_dob} />
          <Display label="Ondernemer" value={partner_is_entrepreneur} />
          <Display label="Bruto jaarinkomen" value={currencyFormatter(partner_income)} />
          {show_partner_pension_income && (
            <Display label="Bruto AOW jaarinkomen" value={currencyFormatter(partner_aow_income)} />
          )}
          <DisplayLink onClick={() => dispatch({type: NAVIGATE, recalculate: currentPageIndex, index: 1})}>
            Gegevens aanpassen
          </DisplayLink>
        </Collapsable>
      )}
      {hasExpenses && (
        <Collapsable title="Financi&#xEB;le verplichtingen">
          {has_student_loan && (
            <>
              {(student_loan_monthly_payment > 0) && (
                <>
                  <Display label="Maandelijkse aflossing studieschuld"
                           value={currencyFormatter(student_loan_monthly_payment)} />
                </>
              )}
              {applicant_has_partner && (partner_student_loan_monthly_payment > 0) && (
                <>
                  <Display label="Partner: maandelijkse aflossing studieschuld"
                           value={currencyFormatter(partner_student_loan_monthly_payment)} />
                </>
              )}
            </>
          )}
          {has_total_credits && <Display label="Totale kredieten" value={currencyFormatter(total_credits)} />}
          {has_partner_alimony && (
            <Display label="Maandelijkse partneralimentatie" value={currencyFormatter(partner_alimony)} />
          )}
          {has_ground_rent && <Display label="Jaarlijkse erfpacht" value={currencyFormatter(ground_rent)} />}
          {has_private_lease && (
            <>
              <Display label="Maandelijkse private lease" value={currencyFormatter(private_lease)} />
            </>
          )}
          {has_partner_private_lease && (
            <>
              <Display label="Maandelijkse private lease (Partner)" value={currencyFormatter(partner_private_lease)} />
            </>
          )}
          <DisplayLink onClick={() => dispatch({type: NAVIGATE, recalculate: currentPageIndex, index: 2})}>
            Gegevens aanpassen
          </DisplayLink>
        </Collapsable>
      )}
      <Collapsable title="Energielabel">
          <Display label="Energielabel" value={(new_regulations ? energy_labels_next_year : energy_labels).find(({value}) => value === energy_label)?.label} />
          <DisplayLink onClick={() => dispatch({type: NAVIGATE, recalculate: currentPageIndex, index: 3})}>
            Gegevens aanpassen
          </DisplayLink>
      </Collapsable>
    </Fragment>
  );
};

export default WizardFinal;
